import { ref, shallowRef, computed, onMounted, watch, nextTick } from 'vue';
import { debounce } from 'lodash-es';
import { useTestMode } from './useTestMode.js';
import { useViewStatePreservation } from './useViewStatePreservation.js';
import apiService from '../apiService';

export function useTierRow() {
  const albums = shallowRef([]);
  const loading = ref(true);
  const tierOrder = ["Now Listening To", "S", "A", "B", "C", "D", "E", "F", "Tiering Herrie", "?", "Not rated yet"];
  const currentTierGrouping = ref('overall'); // Add this
  const selectedSubmitter = ref(null);
  const selectedMainGenre = ref(null);
  const selectedAlbum = ref(null);
  const albumsContainerRef = ref(null);
  const searchTerm = ref('');
  const debouncedSearchTerm = ref('');
  const filterType = ref('ratings'); // Add filterType here

  // Test mode functionality
  const { showTestAlbums } = useTestMode();

  // View state preservation
  const { restoreViewState, captureViewState } = useViewStatePreservation();

  // Debounce search input for performance
  const debouncedSearch = debounce((value) => {
    debouncedSearchTerm.value = value;
  }, 500);
  
  watch(searchTerm, (newValue) => {
    debouncedSearch(newValue);
  });

  const fetchAlbums = async () => {
    try {
      const response = await apiService.fetchAllAlbums();
      // Handle serverless API response structure: {success: true, data: Array}
      let albumsData = [];
      if (response && response.success && Array.isArray(response.data)) {
        albumsData = response.data;
      } else if (Array.isArray(response)) {
        // Fallback for direct array response (legacy compatibility)
        albumsData = response;
      } else {
        console.error('Albums response structure invalid:', response);
        return;
      }
      
      albums.value = albumsData.map(album => ({
        ...album,
        submitted_by: album.submitted_by?.toLowerCase() ?? '',
      }));
      console.log("Fetched albums in tierRow:", albums.value.length, "albums"); // Updated debugging line
    } catch (error) {
      console.error("There was an error fetching the albums!", error);
    } finally {
      loading.value = false;
    }
  };

  const individualTierNames = computed(() => {
    const names = new Set();
    albums.value.forEach(album => {
      Object.keys(album.individual_tiers || {}).forEach(name => names.add(name.toLowerCase()));
    });
    return Array.from(names).sort();
  });

  const submitterNames = computed(() => {
    const names = new Set();
    albums.value.forEach(album => {
      if (album.submitted_by) {
        names.add(album.submitted_by);
      }
    });
    return Array.from(names).sort();
  });

  const availableMainGenres = computed(() => {
    const slugs = new Set();
    albums.value.forEach(a => (a.mainGenres || []).forEach(s => slugs.add(s)));
    return Array.from(slugs).sort();
  });

  function handleAlbumClicked(album) {
    // Capture state before opening album detail modal
    captureViewState({
      viewMode: 'tierListView',
      filters: {
        reviewer: currentTierGrouping.value !== 'overall' ? currentTierGrouping.value : null,
        search: searchTerm.value || null,
        submitter: selectedSubmitter.value || null,
        filterType: filterType.value
      }
    });

    selectedAlbum.value = album;
  }

  function tierClass(tier) {
    // Handle special tier names with spaces/special characters
    const tierClassMap = {
      'Now Listening To': 'tier-now',
      'Not rated yet': 'tier-not',
      'Tiering Herrie': 'tier-tiering',
      '?': 'tier-question'
    };
    
    return tierClassMap[tier] || `tier-${tier.toLowerCase()}`;
  }

  function setTierGrouping(grouping) {
    if (grouping === 'clear') {
      currentTierGrouping.value = 'overall';
      selectedSubmitter.value = null;
      filterType.value = 'ratings'; // Reset filterType when clearing filters
      searchTerm.value = ''; // Clear the search term as well
    } else {
      currentTierGrouping.value = grouping;
    }
  }

  function setSubmitterFilter(submitter) {
    if (submitter === null) {
      selectedSubmitter.value = null;
    } else {
      const submitterLower = submitter.toLowerCase();
      selectedSubmitter.value = selectedSubmitter.value === submitterLower ? null : submitterLower;
    }
  }

  function setMainGenreFilter(slug) {
    selectedMainGenre.value = selectedMainGenre.value === slug ? null : slug;
  }

  const currentViewingText = computed(() => {
    if (selectedSubmitter.value) {
      return `Submitted by ${selectedSubmitter.value}`;
    }
    return currentTierGrouping.value === 'overall' ? 'Overall Rating' : currentTierGrouping.value;
  });

  const filteredAlbumsByTier = computed(() => {
    const tiers = {};
    const searchLowerCase = debouncedSearchTerm.value.toLowerCase();
    albums.value.forEach((album) => {
      // Apply reviewer filter - filter albums BY REVIEWER (only show albums with ratings from this reviewer)
      if (currentTierGrouping.value !== 'overall') {
        // Check for reviewer name case-insensitively
        const reviewerKey = Object.keys(album.individual_tiers || {}).find(
          key => key.toLowerCase() === currentTierGrouping.value.toLowerCase()
        );
        if (!reviewerKey) {
          return; // Skip albums without this reviewer's rating
        }
      }

      // Determine tier to display
      let tier = album.tier;
      if (currentTierGrouping.value !== 'overall') {
        // Find the tier value case-insensitively
        const reviewerKey = Object.keys(album.individual_tiers || {}).find(
          key => key.toLowerCase() === currentTierGrouping.value.toLowerCase()
        );
        tier = reviewerKey ? album.individual_tiers[reviewerKey] : tier;
      }

      // Apply search filter
      const matchesSearch =
        album.artist.toLowerCase().includes(searchLowerCase) ||
        album.title.toLowerCase().includes(searchLowerCase);

      // Apply submitter filter
      const matchesSubmitter = !selectedSubmitter.value || album.submitted_by === selectedSubmitter.value;

      // Apply main genre filter
      const matchesMainGenre = !selectedMainGenre.value || (album.mainGenres || []).includes(selectedMainGenre.value);

      if (matchesSearch && matchesSubmitter && matchesMainGenre) {
        if (!tiers[tier]) tiers[tier] = [];
        tiers[tier].push(album);
      }
    });
    return Object.entries(tiers).sort((a, b) => tierOrder.indexOf(a[0]) - tierOrder.indexOf(b[0]));
  });

  onMounted(async () => {
    await fetchAlbums(); // Fetch albums on mount

    // Restore view state after albums loaded (tierListView only)
    const savedState = restoreViewState();
    if (savedState && savedState.viewMode === 'tierListView') {
      console.log('[TierRow] Restoring tierlist state');

      // Restore filters
      if (savedState.filters.reviewer && savedState.filters.reviewer !== 'overall') {
        currentTierGrouping.value = savedState.filters.reviewer;
      }
      if (savedState.filters.search) {
        searchTerm.value = savedState.filters.search;
      }
      if (savedState.filters.submitter) {
        selectedSubmitter.value = savedState.filters.submitter;
      }
      if (savedState.filters.filterType) {
        filterType.value = savedState.filters.filterType;
      }

      // Restore scroll position after DOM update
      await nextTick();
      window.scrollTo({
        top: savedState.scrollY,
        behavior: 'smooth'
      });

      console.log('[TierRow] State restored');
    }
  });
  
  // Watch for test mode changes and reload data
  watch(showTestAlbums, () => {
    fetchAlbums();
  });

  return {
    albums,
    loading,
    currentViewingText,
    currentTierGrouping,
    filteredAlbumsByTier,
    individualTierNames,
    submitterNames,
    availableMainGenres,
    searchTerm,
    debouncedSearchTerm,
    selectedAlbum,
    selectedSubmitter,
    selectedMainGenre,
    albumsContainerRef,
    handleAlbumClicked,
    tierClass,
    setTierGrouping,
    setSubmitterFilter,
    setMainGenreFilter,
    filterType, // Expose filterType
    fetchAlbums, // Expose for testing
  };
}